import React, { useCallback, useMemo, useState } from "react";
import { Trash2, X } from "lucide-react";

/*
 * Row selection for admin tables.
 *
 * Removing records one at a time is fine for one; clearing a test import of
 * fifty shooters that way is not. The header checkbox selects everything
 * currently listed — which means the filtered set, not the whole database, so a
 * search followed by "select all" is a safe way to target a subset.
 *
 * Selection is kept as ids rather than rows: rows are refetched after every
 * change, and holding stale objects would delete the wrong things. Ids that
 * disappear from the list are dropped on the next render.
 */
export function useTableSelection(visibleIds: string[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Anything no longer listed (filtered out, or already deleted) leaves the set.
  // Array.from rather than spread — the build targets a version without
  // downlevel iteration for Sets.
  const live = useMemo(() => {
    const visible = new Set(visibleIds);
    return new Set(Array.from(selected).filter((id) => visible.has(id)));
  }, [selected, visibleIds]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const visible = new Set(visibleIds);
      const allOn = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      // Toggling off clears only what is listed, so a selection made under a
      // different filter is not silently discarded.
      if (allOn) return new Set(Array.from(prev).filter((id) => !visible.has(id)));
      return new Set(Array.from(prev).concat(visibleIds));
    });
  }, [visibleIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected: live,
    ids: Array.from(live),
    count: live.size,
    isSelected: (id: string) => live.has(id),
    toggle,
    toggleAll,
    clear,
    allSelected: visibleIds.length > 0 && visibleIds.every((id) => live.has(id)),
    someSelected: live.size > 0 && !visibleIds.every((id) => live.has(id)),
  };
}

/** Header checkbox: checked when all listed rows are selected, dashed when some are. */
export function SelectAllBox({
  checked,
  indeterminate,
  onChange,
  label = "Select all rows",
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label?: string;
}) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-line accent-primary"
      checked={checked}
      // The dashed "some selected" state has no HTML attribute — only a DOM property.
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={onChange}
      aria-label={label}
    />
  );
}

export function RowBox({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-line accent-primary"
      checked={checked}
      onChange={onChange}
      // Row clicks usually navigate; selecting must not follow the link.
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
    />
  );
}

/*
 * Naive "+s" produced "matchs". Only the endings that actually occur among the
 * nouns these tables use are handled — there is no need for a general
 * pluraliser, and one would be harder to trust than this.
 */
const plural = (noun: string, n: number) => {
  if (n === 1) return noun;
  if (/(s|x|z|ch|sh)$/i.test(noun)) return `${noun}es`;
  if (/[^aeiou]y$/i.test(noun)) return `${noun.slice(0, -1)}ies`;
  return `${noun}s`;
};

/*
 * Appears only when something is selected, so the table is uncluttered until it
 * is needed. It states the count in words rather than relying on the checkboxes
 * being in view — after scrolling a long table, the header is not.
 */
export function BulkBar({
  count,
  noun,
  onClear,
  onDelete,
  deleteLabel = "Remove selected",
}: {
  count: number;
  /** Singular noun for the rows, e.g. "shooter" — pluralised here. */
  noun: string;
  onClear: () => void;
  onDelete: () => void;
  deleteLabel?: string;
}) {
  if (!count) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-2.5">
      <span className="text-[13px] font-bold text-primary">
        {count} {plural(noun, count)} selected
      </span>
      <button onClick={onClear} className="inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-ink">
        <X size={13} /> Clear
      </button>
      <button
        onClick={onDelete}
        className="ml-auto inline-flex h-9 items-center gap-2 rounded-lg bg-danger px-3.5 text-[13px] font-bold text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98]"
      >
        <Trash2 size={14} /> {deleteLabel}
      </button>
    </div>
  );
}
